import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format } from "date-fns";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputBase,
  LinearProgress,
  Rating,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AssignmentTurnedIn,
  Call,
  CheckCircle,
  Close,
  Email,
  Explore,
  NotInterested,
  Schedule,
  SearchOutlined,
  Sell,
  SmartDisplay,
} from "@mui/icons-material";

import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { ICrewAssignmentDialogProps } from "../types/crew-dashboard";
import { StreamResponse } from "../CrewDashboard";
import { PilotAPIResponse } from "../types/view-pilot";
import { RoleSkillTool } from "../types/add-crew-member";
import {
  PaginationProps,
  Paginator,
} from "../../../components/common/Paginator";
import { TrashIcon } from "../../../components/icons";
import { AxiosError } from "axios";
import { IRoleSkillTool } from "../types/rate-card";
import AssignmentRateDialog from "./AssignmentRateDialog";
import MissingRoleRateDialog from "./MissingRoleRateDialog";
import useAssignmentRateGuard from "../hooks/useAssignmentRateGuard";
import {
  AssignmentRateResolution,
  createPixelPilotJobAndGig,
} from "../utils/assignmentRates";
import { tokens } from "../../../theme";

interface AssignmentResponseProps {
  crew_member: {
    full_name: string;
  };
  id: number;
};
interface CallsheetProps {
  id: number;
  assignment: Array<{
    role: string;
    notes: string | null;
  }>;
  crew_member: PilotAPIResponse;
  roles: IRoleSkillTool[];
};

export const CrewAssignmentDialog: React.FC<ICrewAssignmentDialogProps> = ({
  setShowCrewAssignmentDialog,
  showCrewAssignmentDialog,
  selectedStream,
}) => {
  const [crewOptions, setCrewOptions] = useState<PilotAPIResponse[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleSkillTool[]>([]);
  const [rolesCount, setRolesCount] = useState<number>(0);
  const [stream, setStream] = useState<StreamResponse | null>(null);
  const [currentAssignments, setCurrentAssignments] = useState<
    CallsheetProps[]
  >([]);
  const [selectedRole, setSelectedRole] = useState<RoleSkillTool | null>(null);
  const [searching, setSearching] = useState<string | null>("");
  const [searched, setSearched] = useState<PilotAPIResponse[]>([]);
  const [crewLoading, setCrewLoading] = useState<boolean>(false);
  const [streamLoading, setStreamLoading] = useState<boolean>(false);
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);
  const [rolesLoading, setRolesLoading] = useState<boolean>(false);
  const [callsheetLoading, setCallsheetLoading] = useState<boolean>(false);
  const [crewError, setCrewError] = useState<string | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [callsheetError, setCallsheetError] = useState<string | null>(null);

  const [pilotCount, setPilotCount] = useState<number>(0);

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 0,
    pageSize: 5,
  });

  const [pilotPagination, setPilotPagination] = useState<PaginationProps>({
    page: 0,
    pageSize: 10,
  });

  const navigate = useNavigate();
  const {
    ensureRateForRole,
    missingRateDialogProps,
    assignmentRateDialogProps,
  } = useAssignmentRateGuard();

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const closeAndReset = () => {
    setShowCrewAssignmentDialog(false);
    setCrewOptions([]);
  };

  const fetchCrew = useCallback(async () => {
    setCrewLoading(true);
    setCrewError(null);
    try {
      const apiPage = pilotPagination.page + 1;
      const crew = await apiService.get<{
        count: number;
        results: PilotAPIResponse[];
      }>(`/api/v1/crews/crew-members/?roles=${selectedRole?.id}`, {
        page: apiPage,
        page_size: pilotPagination.pageSize,
      });

      setCrewOptions(crew.data.results);
      setPilotCount(crew.data.count);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to load crew. Please try again.";
        setCrewError(errorMessage);
      } else {
        console.error("Failed to fetch crew members:", error);
      }
      setCrewOptions([]);
    } finally {
      setCrewLoading(false);
    }
  }, [pilotPagination.page, pilotPagination.pageSize, selectedRole?.id]);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      const apiPage = pagination.page + 1;
      const roles = await apiService.get<{
        count: number;
        results: RoleSkillTool[];
      }>(`/api/v1/crews/roles/`, {
        page: apiPage,
        page_size: pagination.pageSize,
      });

      setRoleOptions(roles.data.results);
      setRolesCount(roles.data.count);

      const filterOutProducerRoles = roles.data.results.filter(
        (role) => !role.name.toLowerCase().includes("producer")
      );
      setSelectedRole(filterOutProducerRoles[0]);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to load roles. Please try again.";
        setRolesError(errorMessage);
      } else {
        console.log("Failed to load roles", error);
      }
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (crewOptions.length) {
      const filtering = crewOptions.filter((crew) => {
        const searchedCrew =
          !searching ||
          crew.full_name.toLowerCase().includes(searching.toLowerCase());

        return searchedCrew;
      });

      setSearched(filtering);
    }
  }, [crewOptions, searching, showCrewAssignmentDialog]);

  const fetchStream = useCallback(async () => {
    setStreamLoading(true);
    setStreamError(null);
    try {
      const strm = await apiService.get<StreamResponse>(
        `/api/streams/stream/${selectedStream}/`
      );

      setStream(strm.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch stream. Try again.";
        setStreamError(errorMessage);
      } else {
        console.log("Failed to fetch streams", error);
      }
      setStream(null);
    } finally {
      setStreamLoading(false);
    }
  }, [selectedStream]);

  const fetchCallsheet = useCallback(async () => {
    setCallsheetLoading(true);
    setCallsheetError(null);
    try {
      const callsheet = await apiService.get<{ results: CallsheetProps[] }>(
        `/api/call-sheets/callsheet/?stream=${selectedStream}`,
        { page_size: 25 }
      );

      setCurrentAssignments(callsheet.data.results);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to fetch callsheet. Try again.";
        setCallsheetError(errorMessage);
      } else {
        console.log("Failed to fetch callsheet", error);
      }
      setCurrentAssignments([]);
    } finally {
      setCallsheetLoading(false);
    }
  }, [selectedStream]);

  const completePilotAssignment = async (
    user: number,
    pilot: number,
    resolvedRate: AssignmentRateResolution,
  ) => {
    try {
      const payload = {
        stream_id: stream?.id,
        crew_member_id: pilot,
        assignment: [
          {
            role: selectedRole?.name,
            notes: "",
          },
        ],
        roles_ids: [selectedRole?.id],
      };

      const assignment = await apiService.post<AssignmentResponseProps>(
        `/api/call-sheets/callsheet/`,
        payload
      );

      await createPixelPilotJobAndGig({
        amount: resolvedRate.amount,
        assignedTo: user,
        callsheetId: assignment.data.id,
        crewMemberId: pilot,
        currency: resolvedRate.currency,
        description: stream?.title,
        stream: {
          id: stream?.id ?? 0,
          title: stream?.title ?? "",
          date: String(stream?.date ?? ""),
          start: stream?.start ?? "",
          end: stream?.end ?? "",
        },
      });

      toast.success(
        `Successfully assigned ${assignment.data.crew_member.full_name} as ${payload.assignment[0].role} for the stream: ${stream?.title}`
      );
      fetchCallsheet();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ||
            "Failed to assign pilot. Please try again."
        );
      } else {
        console.error("Failed to assign pilot:", error);
      }
    }
  };

  const handlePilotAssignment = async (user: number, pilot: number) => {
    if (!selectedRole?.id || !selectedRole?.name) {
      toast.error("Please select a role before assigning crew.");
      return;
    }

    if (!stream?.id) {
      toast.error("Stream details are still loading. Please try again.");
      return;
    }

    setAssignmentLoading(true);

    try {
      const hasRate = await ensureRateForRole(
        {
          id: selectedRole.id,
          name: selectedRole.name,
          description: selectedRole.description,
        },
        async (resolvedRate) => {
          setAssignmentLoading(true);
          try {
            await completePilotAssignment(user, pilot, resolvedRate);
          } finally {
            setAssignmentLoading(false);
          }
        },
        {
          effectiveDate: String(stream.date ?? ""),
        },
      );

      if (!hasRate) {
        setAssignmentLoading(false);
      }
    } catch (error) {
      setAssignmentLoading(false);
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ||
            "Failed to validate the selected role rate. Please try again.",
        );
      } else {
        toast.error("Failed to validate the selected role rate. Please try again.");
      }
    }
  };

  const handleUnAssignment = async (
    id: number,
    pilot: string,
    assignment: string
  ) => {
    setAssignmentLoading(true);
    try {
      await apiService.delete(`/api/call-sheets/callsheet/${id}/`);

      toast.success(
        `Successfully unassigned Pixel Pilot: ${pilot} as ${assignment} for the stream: ${stream?.title}`
      );
      fetchCallsheet();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ||
            "Failed to unassign pilot. Please try again."
        );
      } else {
        console.error("Failed to unassign pilot:", error);
      }
    } finally {
      setAssignmentLoading(false);
    }
  };

  const retryStream = () => {
    fetchStream();
  };

  const retryCrew = () => {
    fetchCrew();
  };

  const retryCallsheet = () => {
    fetchCallsheet();
  };

  const retryRoles = () => {
    fetchRoles();
  };

  useEffect(() => {
    if (selectedStream) {
      fetchStream();
      fetchCallsheet();
    }
  }, [fetchCallsheet, fetchStream, selectedStream]);

  useEffect(() => {
    if (selectedRole) {
      fetchCrew();
    };
  }, [fetchCrew, selectedRole]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const checkAssignability = (pilot: number) => {
    let currentAppearances = [];
    currentAppearances = currentAssignments?.filter(
      (ca) => ca.crew_member.id === pilot
    );

    if (
      currentAppearances.some((ca) => {
        const matchesRole = ca.roles?.some(
          (role) => role.id === selectedRole?.id,
        );
        const matchesAssignment = ca.assignment?.some(
          (assignment) =>
            assignment.role?.trim().toLowerCase() ===
            selectedRole?.name.trim().toLowerCase(),
        );

        return matchesRole || matchesAssignment;
      })
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <Dialog
      open={showCrewAssignmentDialog}
      onClose={closeAndReset}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Assign Crew
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container sx={{ my: 1 }} spacing={2}>
          <Grid size={5}>
            {streamLoading ? (
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Chip
                  label={
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <CircularProgress color={"primary"} size={16} />
                      <i>Fetching Stream.</i>
                    </Box>
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    borderColor: colors.primary[800],
                  }}
                />
              </Box>
            ) : streamError ? (
              <Alert severity="error" sx={{ alignItems: "center" }}>
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  {streamError}
                  <Button
                    onClick={() => retryStream()}
                    sx={{
                      color: colors.primary[400],
                      backgroundColor: colors.primary[900],
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              </Alert>
            ) : !stream ? (
              <Alert severity="warning" sx={{ alignItems: "center" }}>
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  {"Could not fetch stream."}
                  <Button
                    onClick={() => retryStream()}
                    sx={{
                      color: colors.primary[400],
                      backgroundColor: colors.primary[900],
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              </Alert>
            ) : (
              <>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  sx={{
                    alignItems: "flex-start",
                    backgroundColor: colors.primary[800],
                    padding: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1,
                        backgroundColor: colors.primary[400],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {assignmentLoading ? (
                        <CircularProgress sx={{ color: "#FFF" }} size={24} />
                      ) : (
                        <SmartDisplay sx={{ color: "#FFF", fontSize: 32 }} />
                      )}
                    </Box>
                    <Box>
                      <Typography variant={"subtitle1"} fontWeight={600}>
                        {stream.title}
                      </Typography>
                      <Chip
                        size={"small"}
                        label={stream.booking?.booking_type}
                        sx={{
                          borderRadius: 20,
                          backgroundColor: colors.primary[900],
                          mt: 0.4,
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      backgroundColor: colors.primary[900],
                      borderRadius: 1,
                      padding: 0.8,
                    }}
                    mt={1}
                  >
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      mb={0.4}
                    >
                      <Explore sx={{ fontSize: 17 }} />
                      <Typography variant={"subtitle2"}>
                        {stream.event_details?.venue.name}
                      </Typography>
                    </Box>
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      mb={0.4}
                    >
                      <Schedule sx={{ fontSize: 17 }} />
                      <Typography variant={"subtitle2"}>{`${
                        stream.start
                      } - ${format(stream.date, "dd MMM yyyy")}`}</Typography>
                    </Box>
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <Sell sx={{ fontSize: 17 }} />
                      <Typography
                        variant={"subtitle2"}
                      >{`USD${stream.booking?.package_item.price} - ${stream.booking?.package_item.name}`}</Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    alignItems: "center",
                    mt: 1,
                    backgroundColor: colors.primary[800],
                    padding: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography variant={"caption"}>Assigned:</Typography>
                    <Typography variant={"caption"}>{`8/8`}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.floor((8 / 8) * 100)}
                  />

                  {rolesLoading ? (
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <Chip
                        label={
                          <Box display={"flex"} alignItems={"center"} gap={1}>
                            <CircularProgress color={"primary"} size={16} />
                            <i>Fetching Roles.</i>
                          </Box>
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 1.5,
                          borderColor: colors.primary[800],
                        }}
                      />
                    </Box>
                  ) : rolesError ? (
                    <Alert severity="error" sx={{ alignItems: "center" }}>
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        {rolesError}
                        <Button
                          onClick={() => retryRoles()}
                          sx={{
                            color: colors.primary[400],
                            backgroundColor: colors.primary[900],
                          }}
                        >
                          Retry
                        </Button>
                      </Box>
                    </Alert>
                  ) : !roleOptions ? (
                    <Alert severity="warning" sx={{ alignItems: "center" }}>
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        {"Could not fetch roles."}
                        <Button
                          onClick={() => retryRoles()}
                          sx={{
                            color: colors.primary[400],
                            backgroundColor: colors.primary[900],
                          }}
                        >
                          Retry
                        </Button>
                      </Box>
                    </Alert>
                  ) : (
                    <Box mt={2}>
                      <Typography variant={"caption"}>{`Roles`}</Typography>
                      {roleOptions.map((role) => {
                        return (
                          <Box
                            key={role.id}
                            display={"flex"}
                            onClick={() => setSelectedRole(role)}
                            justifyContent={"space-between"}
                            sx={{
                              padding: 1,
                              pointerEvents: role.name
                                .toLowerCase()
                                .includes("producer")
                                ? "none"
                                : "auto",
                              cursor: "pointer",
                              borderWidth: 1,
                              borderStyle: "solid",
                              borderColor:
                                selectedRole?.id === role.id
                                  ? colors.primary[400]
                                  : colors.primary[900],
                              mb: 1,
                              borderRadius: 2,
                              backgroundColor: colors.primary[900],
                            }}
                          >
                            <Box display={"flex"} alignItems={"center"} gap={1}>
                              {role.name.toLowerCase().includes("producer") ? (
                                <NotInterested
                                  sx={{ color: colors.primary[800] }}
                                />
                              ) : (
                                <CheckCircle
                                  sx={{ color: colors.primary[400] }}
                                />
                              )}
                              {/* {isComplete ? (
                                  <CheckCircle sx={{ color: colors.primary[400] }} />
                                ) : (
                                  <RadioButtonUnchecked sx={{ color: colors.primary[400] }} />
                                )} */}
                              <Typography variant={"caption"}>
                                {role.name}
                              </Typography>
                            </Box>
                            {/* <Typography variant={'caption'}>{`${role.completion}/${role.required}`}</Typography> */}
                          </Box>
                        );
                      })}
                      {pagination ? (
                        <Paginator
                          count={rolesCount}
                          pagination={pagination}
                          setPagination={setPagination}
                        />
                      ) : null}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Grid>

          <Grid size={7}>
            <Box
              display={"flex"}
              sx={{
                padding: 1,
                alignItems: "center",
                borderRadius: 2,
                backgroundColor: colors.primary[800],
                justifyContent: "space-between",
              }}
            >
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: colors.primary[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AssignmentTurnedIn sx={{ color: "#FFF", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant={"subtitle2"}>
                    {"Quick Assignment"}
                  </Typography>
                  <Typography variant={"caption"}>
                    {"Auto fill with best available crew"}
                  </Typography>
                </Box>
              </Box>
              <Button
                disabled
                sx={{
                  color: colors.primary[400],
                  backgroundColor: colors.primary[900],
                }}
              >
                Auto Assign
              </Button>
            </Box>

            <Box sx={{ my: 1 }}>
              <Box
                sx={{
                  backgroundColor: "",
                  border: "1px solid",
                  borderRadius: 2,
                  borderColor: colors.primary[300],
                  display: "flex",
                  alignItems: "center",
                  py: 0.4,
                  pl: 1,
                  flex: 1,
                  minWidth: 320,
                }}
              >
                <SearchOutlined
                  sx={{
                    fontSize: 24,
                    mr: 1,
                    color: colors.primary[300],
                  }}
                />
                <InputBase
                  placeholder="Search by crew member name..."
                  sx={{
                    padding: 0.4,
                    flex: 1,
                    color: "#757575",
                  }}
                  onChange={(e) => setSearching(e.target.value)}
                />
              </Box>
            </Box>

            {(crewLoading && !selectedRole) ? (
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Chip
                  label={
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <CircularProgress color={"primary"} size={16} />
                      <i>Fetching Crew Members.</i>
                    </Box>
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    borderColor: colors.primary[800],
                  }}
                />
              </Box>
            ) : (crewError && selectedRole) ? (
              <Alert severity="error" sx={{ alignItems: "center" }}>
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  {crewError}
                  <Button
                    onClick={() => retryCrew()}
                    sx={{
                      color: colors.primary[400],
                      backgroundColor: colors.primary[900],
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Box>
                <Grid container spacing={2}>
                  {searched &&
                    searched.map((crw) => {
                      const isSingleResult = searched.length === 1;

                      return (
                        <Grid
                          size={{ xs: 12, sm: isSingleResult ? 12 : 6, md: isSingleResult ? 12 : 4 }}
                          key={crw.id}
                          sx={{
                            alignItems: "center",
                            backgroundColor: colors.primary[800],
                            padding: 2,
                            borderRadius: 2,
                          }}
                        >
                          <Box
                            display={"flex"}
                            flexDirection={"column"}
                            alignItems={"center"}
                            gap={1}
                          >
                            <Avatar
                              src={
                                crw.user.profile_picture
                                  ? crw.user.profile_picture
                                  : `./placeholder.svg`
                              }
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: 50,
                              }}
                            />
                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              flexDirection={"column"}
                            >
                              <Typography
                                textAlign={"center"}
                                variant={"subtitle2"}
                              >
                                {crw.full_name}
                              </Typography>
                              <Typography
                                textAlign={"center"}
                                variant={"caption"}
                                color={"text.secondary"}
                                sx={{
                                  maxWidth: "100%",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {crw.user.email || "No email address"}
                              </Typography>
                              <Rating
                                size="small"
                                value={
                                  crw.average_rating ? crw.average_rating : 0
                                }
                                precision={0.1}
                                readOnly
                              />
                            </Box>
                          </Box>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            flexDirection={"column"}
                          >
                            <Chip
                              label={"Available"}
                              size={"small"}
                              sx={{
                                my: 1,
                                borderRadius: 20,
                              }}
                            />
                            <Box sx={{ display: "flex", mb: 0.4, gap: 1 }}>
                              <Tooltip
                                title={
                                  crw.user.phone_number || "No phone number"
                                }
                              >
                                <IconButton
                                  disabled={!crw.user.phone_number}
                                  onClick={() =>
                                    crw.user.phone_number &&
                                    window.open(`tel:${crw.user.phone_number}`)
                                  }
                                  sx={{
                                    color: crw.user.phone_number
                                      ? colors.primary[400]
                                      : colors.primary[800],
                                    opacity: crw.user.phone_number ? 1 : 0.5,
                                    "&:hover": {
                                      color: crw.user.phone_number
                                        ? colors.primary[400]
                                        : colors.primary[800],
                                    },
                                  }}
                                >
                                  <Call fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={crw.user.email || "No email address"}
                              >
                                <IconButton
                                  disabled={!crw.user.email}
                                  onClick={() =>
                                    crw.user.email &&
                                    window.open(`mailto:${crw.user.email}`)
                                  }
                                  sx={{
                                    color: crw.user.email
                                      ? colors.primary[400]
                                      : colors.primary[800],
                                    opacity: crw.user.email ? 1 : 0.5,
                                    "&:hover": {
                                      color: crw.user.email
                                        ? colors.primary[400]
                                        : colors.primary[800],
                                    },
                                  }}
                                >
                                  <Email fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Button
                              fullWidth
                              disabled={
                                assignmentLoading || !checkAssignability(crw.id)
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePilotAssignment(crw.user.id, crw.id);
                              }}
                              variant={"contained"}
                            >
                              {"Assign"}
                            </Button>
                          </Box>
                        </Grid>
                      );
                    })}
                </Grid>
              </Box>
            )}
            {pagination && (
              <Paginator
                count={pilotCount}
                pagination={pilotPagination}
                setPagination={setPilotPagination}
              />
            )}
          </Grid>
        </Grid>

        {/* CURRENT ASSIGNMENTS */}
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderWidth: 1,
            borderTopStyle: "solid",
            borderColor: "divider",
          }}
        >
          <Box display={"flex"} alignItems={"center"} mb={1} gap={1}>
            <Typography variant={"subtitle2"}>Current Assignments</Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              backgroundColor: colors.primary[800],
              mt: 1,
              padding: 1,
              borderRadius: 2,
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                mb: 1,
                bgcolor: colors.primary[800],
                padding: 0.4,
                borderRadius: 1,
              }}
            >
              <Grid size={3} sx={{ textTransform: "uppercase" }}>
                <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
                  Assignment
                </Typography>
              </Grid>
              <Grid size={3} sx={{ textTransform: "uppercase" }}>
                <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
                  Pilot Name
                </Typography>
              </Grid>
              <Grid size={3.5} sx={{ textTransform: "uppercase" }}>
                <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
                  Email
                </Typography>
              </Grid>
              <Grid size={1.5} sx={{ textTransform: "uppercase" }}>
                <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
                  Phone
                </Typography>
              </Grid>
              <Grid size={1} sx={{ textTransform: "uppercase" }}>
                <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
              </Grid>
            </Grid>
            {callsheetLoading ? (
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Chip
                  label={
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <CircularProgress color={"primary"} size={16} />
                      <i>Fetching Callsheet.</i>
                    </Box>
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    borderColor: colors.primary[800],
                  }}
                />
              </Box>
            ) : callsheetError ? (
              <Alert severity="error" sx={{ alignItems: "center" }}>
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  {callsheetError}
                  <Button
                    onClick={() => retryCallsheet()}
                    sx={{
                      color: colors.primary[400],
                      backgroundColor: colors.primary[900],
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              </Alert>
            ) : !currentAssignments ? (
              <Alert severity="warning" sx={{ alignItems: "center" }}>
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  {"Could not fetch the current callsheet."}
                  <Button
                    onClick={() => retryCallsheet()}
                    sx={{
                      color: colors.primary[400],
                      backgroundColor: colors.primary[900],
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              </Alert>
            ) : (
              currentAssignments.map((ca, index) => {
                const rowBg = index % 2 === 0 ? colors.primary[900] : "transparent";
                return (
                  <Grid
                    container
                    key={ca.id}
                    display={"flex"}
                    alignItems={"center"}
                    spacing={2}
                    sx={{
                      mb: 1,
                      bgcolor: rowBg,
                      padding: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Grid size={3}>
                      <Typography variant={"body2"}>
                        {ca.roles[0]?.name}
                      </Typography>
                    </Grid>
                    <Grid size={3}>
                      <Typography variant={"body2"}>
                        {ca.crew_member.full_name}
                      </Typography>
                    </Grid>
                    <Grid size={3.5}>
                      <Typography variant={"body2"}>
                        {ca.crew_member.user.email}
                      </Typography>
                    </Grid>
                    <Grid size={1.5}>
                      <Typography variant={"body2"}>
                        {ca.crew_member.user.phone_number}
                      </Typography>
                    </Grid>
                    <Grid size={1}>
                      <IconButton
                        disabled={assignmentLoading}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUnAssignment(
                            ca.id,
                            ca.crew_member.full_name,
                            ca.roles[0]?.name
                          );
                        }}
                      >
                        <TrashIcon color="error" sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeAndReset}
          sx={{
            color: colors.primary[400],
            backgroundColor: colors.primary[800],
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => {
            closeAndReset();
            navigate(`/stream-management/view/${selectedStream}/callsheet`);
          }}
          variant={"contained"}
          sx={{
            color: "#FFF",
            backgroundColor: colors.primary[400],
          }}
        >
          Continue
        </Button>
      </DialogActions>
      <MissingRoleRateDialog {...missingRateDialogProps} />
      <AssignmentRateDialog {...assignmentRateDialogProps} />
    </Dialog>
  );
};

export default CrewAssignmentDialog;
